/**
 * @file Portfolio theme model for the Creator Toolkit editor (EPD-10089).
 *
 * A portfolio "theme" bundles the colours, fonts, type sizes and layout choices
 * a creator makes in the customize panel. It is persisted as a JSON string under
 * the `portfolioTheme` key of `profiles.metadata` (the metadata catchall permits
 * string values), alongside a `portfolioPublished` boolean.
 *
 * Presets and their values live in `theme-presets.ts`, which is generated from
 * the tweakcn registry by `theme-presets.generate.ts` (OKLCH → hex) — re-run that
 * script to refresh them rather than hand-editing the generated file.
 */

import {
  DEFAULT_PORTFOLIO_LOCATION_DISPLAY,
  PORTFOLIO_LOCATION_DISPLAYS,
} from "@repo/core";
import { z } from "zod";
import {
  getHiddenSections,
  parseProfileMetadata,
  serializeProfileMetadata,
  type ProfileMetadata,
} from "@/lib/utils/profile-metadata";
import {
  CATALOG_PLATFORMS,
  demographicMetricIdsFor,
  type CatalogPlatform,
  type HiddenMetricIds,
} from "@/lib/portfolio/metric-catalog";
import {
  PORTFOLIO_THEME_IDS,
  PRESET_COLORS,
  type PortfolioThemeId,
} from "@/lib/portfolio/theme-presets";
// One hex parser (with #abc shorthand expansion) for the runtime colour helpers and the contrast rule.
import { hexToRgb, readableTextColor } from "@/lib/portfolio/theme-contrast";

// The single WCAG contrast rule for an accent's button text, shared with the
// preset generator so runtime and generated preset values never drift apart.
export { accentTextFor } from "@/lib/portfolio/theme-contrast";

// Theme presets (ids, labels, palettes) are curated from the tweakcn registry
// and generated into theme-presets.ts (EPD-10132). Re-exported here so the rest
// of the app keeps importing them from "@/lib/portfolio/theme".
export {
  PORTFOLIO_THEME_IDS,
  PORTFOLIO_THEME_META,
} from "@/lib/portfolio/theme-presets";
export type {
  PortfolioThemeId,
  PresetColors,
  PresetMeta,
} from "@/lib/portfolio/theme-presets";

// ============================================================================
// Types
// ============================================================================

/** Sections of the portfolio the creator can show/hide. */
export const PORTFOLIO_SECTIONS = [
  "content",
  "stats",
  "highlights",
  "rates",
  "contact",
] as const;

// Section ids themes used to store that no longer have a toggle (EPD-12034).
// "social" was the header's social icons, now always shown; "audience" was the
// demographics block, now driven only by the Metrics Shown picker. Stored
// themes still carry them, so the schema strips them rather than failing.
const RETIRED_SECTIONS: readonly string[] = ["social", "audience"];

export type PortfolioSection = (typeof PORTFOLIO_SECTIONS)[number];

// Sections added after themes were already being saved, and the section each
// should slot in front of when migrating an older stored order. Anything not
// listed here is appended to the end.
const MIGRATE_BEFORE: Partial<Record<PortfolioSection, PortfolioSection>> = {
  rates: "contact",
};

/**
 * Complete a stored section order: keep the saved order, insert newer sections
 * listed in MIGRATE_BEFORE ahead of their anchor (when present), and append any
 * other missing section to the end.
 */
export function withMissingSections<T>(
  order: readonly T[],
): Array<T | PortfolioSection> {
  const next: Array<T | PortfolioSection> = [...order];
  for (const section of PORTFOLIO_SECTIONS) {
    if (next.includes(section)) continue;
    const anchor = MIGRATE_BEFORE[section];
    const at = anchor ? next.indexOf(anchor) : -1;
    if (at >= 0) next.splice(at, 0, section);
    else next.push(section);
  }
  return next;
}

/**
 * A hex colour: 3, 6, or 8 digits (`#abc`, `#aabbcc`, `#aabbccdd`). The 8-digit
 * form is allowed because derived tokens (e.g. muted text) append an alpha pair.
 */
export const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** A hex a creator types directly — 3 or 6 digits only (no alpha). */
export const HEX_INPUT_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const hexColor = z.string().regex(HEX_COLOR_RE);

const portfolioThemeObjectSchema = z.object({
  // The preset the creator started from (the picker keeps highlighting it even
  // after granular colour/font edits are layered on top). `.catch("default")`
  // coerces an unknown id (e.g. a preset that has since been removed/renamed)
  // to "default" WITHOUT failing the whole-object parse — so the rest of the
  // stored theme (contactEmail, sectionOrder, show, hiddenMetricIds, custom
  // colours) survives instead of the entire theme resetting to the default.
  presetId: z.enum(PORTFOLIO_THEME_IDS).catch("default"),
  // Colours — validated so a partial value (e.g. "#12") can never be stored or
  // fed to the preview's CSS variables.
  bg: hexColor,
  surface: hexColor,
  text: hexColor,
  headingColor: hexColor,
  mutedText: hexColor,
  border: hexColor,
  accent: hexColor,
  accentText: hexColor,
  heroSolid: hexColor,
  // Fonts
  fontHeading: z.string(),
  fontBody: z.string(),
  // Type sizes (px)
  h1Size: z.number(),
  h2Size: z.number(),
  h3Size: z.number(),
  bodySize: z.number(),
  // Layout
  contactEmail: z.string(),
  // Migrate stored themes forward: a theme saved before a section existed (e.g.
  // "stats") is missing it from sectionOrder/show. z.record over an enum is
  // exhaustive, so without this a legacy `show` would fail validation and the
  // whole theme would fall back to the default, discarding the creator's edits.
  // Append missing sections to the order and default missing visibility to shown.
  sectionOrder: z.preprocess(
    (val) =>
      Array.isArray(val)
        ? withMissingSections(withoutRetiredSections(val))
        : val,
    z.array(z.enum(PORTFOLIO_SECTIONS)),
  ),
  show: z.preprocess(
    (val) => {
      if (!val || typeof val !== "object" || Array.isArray(val)) return val;
      const stored: Record<string, unknown> = { ...val };
      const show: Record<string, unknown> = {};
      for (const section of PORTFOLIO_SECTIONS) {
        show[section] = stored[section] ?? true;
      }
      // Recent Posts (content) is newer than the other sections and lives in the
      // Social Accounts section. A theme saved before it existed must not
      // re-expose the block on a creator who had hidden the section: inherit
      // from the stats/audience toggles rather than defaulting to shown.
      if (!("content" in stored)) {
        show.content = stored.stats !== false || legacyAudienceShown(stored);
      }
      // Retired ids ("social", "audience") aren't copied, so the next save no
      // longer stores them.
      return show;
    },
    z.record(z.enum(PORTFOLIO_SECTIONS), z.boolean()),
  ),
  // Per-network metric ids the creator hid in the "Metrics Shown" picker
  // (EPD-12113; replaces the EPD-10133 allow-list, migrated on read below). An
  // opt-out list, so a platform absent from the map — and any stat added to
  // the catalog later — shows by default. A non-object (corrupt value) resets
  // to "nothing hidden"; unknown platforms, non-array values and non-string
  // entries are dropped rather than failing the whole theme.
  hiddenMetricIds: z
    .preprocess(
      sanitizeHiddenMetricIds,
      z.partialRecord(z.enum(CATALOG_PLATFORMS), z.array(z.string())),
    )
    .default({}),
  // Profile channel ids the creator chose not to feature (EPD-12194), applied
  // to the header icons and the Social Accounts block. An opt-out list, so a
  // theme saved before this existed — and any account connected later — shows
  // by default. A non-array (corrupt value) resets to "all shown"; non-string
  // entries are dropped rather than failing the whole theme.
  hiddenChannelIds: z
    .preprocess(
      (val) =>
        Array.isArray(val)
          ? val.filter((id): id is string => typeof id === "string")
          : [],
      z.array(z.string()),
    )
    .default([]),
  // The order the creator dragged their accounts into (EPD-12481), as profile
  // channel ids; applied to the header icons and the Social Accounts tabs.
  // Accounts not in it (a theme saved before this existed, or an account
  // connected later) follow in connected order. A non-array (corrupt value)
  // resets to connected order; non-string entries are dropped.
  channelOrder: z
    .preprocess(
      (val) =>
        Array.isArray(val)
          ? val.filter((id): id is string => typeof id === "string")
          : [],
      z.array(z.string()),
    )
    .default([]),
  // How much of the creator's location the header shows (EPD-12486). A theme
  // saved before this existed, or an unknown value, shows state and country.
  // The API applies the saved value to the published portfolio itself.
  locationDisplay: z
    .enum(PORTFOLIO_LOCATION_DISPLAYS)
    .catch(DEFAULT_PORTFOLIO_LOCATION_DISPLAY),
});

// The legacy allow-list is inverted first, so the retired Audience migration
// only ever adds to the deny-list form.
const portfolioThemeSchema = z.preprocess(
  (val) => migrateRetiredAudience(migrateLegacyMetricSelections(val)),
  portfolioThemeObjectSchema,
);

export type PortfolioTheme = z.infer<typeof portfolioThemeSchema>;

/**
 * Whether a stored `show` map had Audience demographics on. A theme saved
 * before the toggle existed inherited the Social Metrics (stats) toggle, so a
 * creator who hid stats never had demographics exposed.
 */
function legacyAudienceShown(show: object): boolean {
  if ("audience" in show) return show.audience !== false;
  return !("stats" in show) || show.stats !== false;
}

/**
 * Drop retired section ids from a stored order (EPD-12034). The Social
 * Accounts block renders at whichever of its sections comes first, so when
 * "audience" sat ahead of "stats", "stats" takes its slot — the block stays
 * where the creator put it.
 */
function withoutRetiredSections(order: readonly unknown[]): unknown[] {
  const next = [...order];
  const audience = next.indexOf("audience");
  const stats = next.indexOf("stats");
  if (audience >= 0 && stats > audience) {
    next.splice(stats, 1);
    next[audience] = "stats";
  }
  return next.filter(
    (id) => typeof id !== "string" || !RETIRED_SECTIONS.includes(id),
  );
}

/** Keep only well-formed hidden-metric entries (see `hiddenMetricIds`). */
function sanitizeHiddenMetricIds(val: unknown): HiddenMetricIds {
  if (!val || typeof val !== "object" || Array.isArray(val)) return {};
  const stored: Record<string, unknown> = { ...val };
  const hidden: HiddenMetricIds = {};
  for (const platform of CATALOG_PLATFORMS) {
    const ids = stored[platform];
    if (Array.isArray(ids)) {
      hidden[platform] = ids.filter(
        (id): id is string => typeof id === "string",
      );
    }
  }
  return hidden;
}

/**
 * The catalog's metric ids when the allow-list was retired (EPD-12113). They
 * haven't changed since the picker shipped (#1217), so every stored
 * `metricSelections` was saved against exactly this set. Frozen on purpose:
 * don't add new catalog ids here.
 */
const LEGACY_METRIC_IDS: Record<CatalogPlatform, readonly string[]> = {
  instagram: [
    "ig.followers",
    "ig.totalPosts",
    "ig.authenticity",
    "ig.engagementRate",
    "ig.avgComments",
    "ig.saves",
    "ig.likesToComment",
    "ig.reach",
    "ig.impressionRate",
    "ig.avgStoryImpressions",
    "ig.storyImpressionRate",
    "ig.demo.gender",
    "ig.demo.age",
    "ig.demo.country",
    "ig.demo.city",
  ],
  tiktok: [
    "tt.followers",
    "tt.totalVideos",
    "tt.engagementRate",
    "tt.totalLikes",
    "tt.avgComments",
    "tt.typicalViews",
    "tt.demo.age",
    "tt.demo.country",
  ],
  youtube: [
    "yt.subscribers",
    "yt.totalVideos",
    "yt.engagementRate",
    "yt.typicalViews",
    "yt.avgComments",
    "yt.demo.age",
    "yt.demo.gender",
    "yt.demo.country",
  ],
  pinterest: [
    "pt.followers",
    "pt.engagementRate",
    "pt.avgSavesPerPin",
    "pt.avgCommentsPerPin",
    "pt.avgClicksPerPin",
  ],
};

/**
 * Convert the legacy `metricSelections` allow-list (EPD-10133) into
 * `hiddenMetricIds` (EPD-12113): each saved platform hides whatever catalog
 * metric it didn't allow; unknown ids in the allow-list are ignored, and an
 * unsaved platform hides nothing. `metricSelections` is dropped, so the next
 * save writes only the deny-list.
 *
 * It inverts against `LEGACY_METRIC_IDS`, the frozen set every stored
 * allow-list was saved against, never the live catalog. A legacy row the
 * creator never re-saves is converted on every read, so inverting against the
 * live catalog would hide each stat added to it later.
 */
function migrateLegacyMetricSelections(val: unknown): unknown {
  if (!val || typeof val !== "object" || Array.isArray(val)) return val;
  if (!("metricSelections" in val)) return val;
  const { metricSelections: raw, ...rest } = val;
  // A theme already carrying the deny-list keeps it.
  if ("hiddenMetricIds" in rest) return rest;
  const allowed: Record<string, unknown> =
    raw && typeof raw === "object" && !Array.isArray(raw) ? { ...raw } : {};
  const hidden: HiddenMetricIds = {};
  for (const platform of CATALOG_PLATFORMS) {
    const ids = allowed[platform];
    if (!Array.isArray(ids)) continue;
    hidden[platform] = LEGACY_METRIC_IDS[platform].filter(
      (id) => !ids.includes(id),
    );
  }
  return { ...rest, hiddenMetricIds: hidden };
}

/**
 * Carry a retired Audience demographics "off" into Metrics Shown (EPD-12034).
 * Demographics now show per the Metrics Shown picker alone, so a creator who
 * had hidden the section gets every demographic metric hidden instead — still
 * off, and re-enabled from Metrics Shown. Runs after the legacy allow-list is
 * inverted, so it only adds each platform's demographic ids to whatever that
 * platform already hides.
 */
function migrateRetiredAudience(val: unknown): unknown {
  if (!val || typeof val !== "object" || Array.isArray(val)) return val;
  if (!("show" in val)) return val;
  const { show } = val;
  if (!show || typeof show !== "object" || Array.isArray(show)) return val;
  // Only themes saved before EPD-12034 carry the retired ids; a newer theme with
  // Social metrics off must not lose its demographic picks on every load.
  if (!("social" in show) && !("audience" in show)) return val;
  if (legacyAudienceShown(show)) return val;
  const stored = sanitizeHiddenMetricIds(
    "hiddenMetricIds" in val ? val.hiddenMetricIds : undefined,
  );
  const hidden: HiddenMetricIds = {};
  for (const platform of CATALOG_PLATFORMS) {
    const ids = new Set(stored[platform] ?? []);
    for (const id of demographicMetricIdsFor(platform)) ids.add(id);
    hidden[platform] = [...ids];
  }
  return { ...val, hiddenMetricIds: hidden };
}

const DEFAULT_TYPE = { h1Size: 32, h2Size: 24, h3Size: 16, bodySize: 14 };

const DEFAULT_SECTION_ORDER: PortfolioSection[] = [
  "content",
  "stats",
  "highlights",
  "rates",
  "contact",
];

const DEFAULT_SHOW: Record<PortfolioSection, boolean> = {
  content: true,
  stats: true,
  highlights: true,
  rates: true,
  contact: true,
};

export const DEFAULT_PORTFOLIO_THEME_ID: PortfolioThemeId = "default";

/** Build a full theme from a preset id (colours + fonts + canonical defaults). */
export function buildPortfolioTheme(id: PortfolioThemeId): PortfolioTheme {
  return {
    presetId: id,
    ...PRESET_COLORS[id],
    ...DEFAULT_TYPE,
    contactEmail: "",
    sectionOrder: [...DEFAULT_SECTION_ORDER],
    show: { ...DEFAULT_SHOW },
    hiddenMetricIds: {},
    hiddenChannelIds: [],
    channelOrder: [],
    locationDisplay: DEFAULT_PORTFOLIO_LOCATION_DISPLAY,
  };
}

/**
 * Switch the current theme to a preset's design (colours, fonts, type sizes)
 * while keeping the creator's content and layout — contact email, section
 * order/visibility, hidden metrics, hidden accounts, account order and the
 * location setting aren't part of a preset's look.
 */
export function applyPortfolioPreset(
  current: PortfolioTheme,
  id: PortfolioThemeId,
): PortfolioTheme {
  return {
    ...buildPortfolioTheme(id),
    contactEmail: current.contactEmail,
    sectionOrder: [...current.sectionOrder],
    show: { ...current.show },
    hiddenMetricIds: structuredClone(current.hiddenMetricIds),
    hiddenChannelIds: [...current.hiddenChannelIds],
    channelOrder: [...current.channelOrder],
    locationDisplay: current.locationDisplay,
  };
}

/**
 * Drop the channels the creator hid from the portfolio (EPD-12194). Works on
 * both the profile's public channels (`id`) and the social-stats rows
 * (`channelId`), which carry the same profile channel id.
 */
export function visibleChannels<
  T extends { id: string } | { channelId: string },
>(items: readonly T[], hiddenChannelIds: readonly string[]): T[] {
  if (hiddenChannelIds.length === 0) return [...items];
  const hidden = new Set(hiddenChannelIds);
  return items.filter(
    (item) => !hidden.has("channelId" in item ? item.channelId : item.id),
  );
}

/**
 * Sort channels into the creator's order (EPD-12481): ids in `channelOrder`
 * first, in that order, then everything else in its existing (connected)
 * order. Works on both the profile's public channels (`id`) and the
 * social-stats rows (`channelId`), like `visibleChannels`.
 */
export function orderedChannels<
  T extends { id: string } | { channelId: string },
>(items: readonly T[], channelOrder: readonly string[]): T[] {
  if (channelOrder.length === 0) return [...items];
  const rank = new Map(channelOrder.map((id, index) => [id, index]));
  const rankOf = (item: T) =>
    rank.get("channelId" in item ? item.channelId : item.id) ??
    Number.MAX_SAFE_INTEGER;
  // Array.prototype.sort is stable, so unranked items keep their order.
  return [...items].sort((a, b) => rankOf(a) - rankOf(b));
}

/** The single canonical default state — what "Reset to default" restores. */
export const DEFAULT_PORTFOLIO_THEME: PortfolioTheme = buildPortfolioTheme(
  DEFAULT_PORTFOLIO_THEME_ID,
);

// ============================================================================
// Colour helpers (ported from the design prototype)
// ============================================================================

/** Perceived-luminance test — true for light colours (dark text reads better). */
export function isLight(hex: string): boolean {
  if (!HEX_COLOR_RE.test(hex)) return true;
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 160;
}

/** Lighten (positive percent) or darken (negative) a hex colour. */
export function shade(hex: string, percent: number): string {
  if (!HEX_COLOR_RE.test(hex)) return hex;
  const { r, g, b } = hexToRgb(hex);
  const adjust = (v: number) =>
    Math.max(0, Math.min(255, v + Math.round((255 * percent) / 100)));
  return (
    "#" +
    [adjust(r), adjust(g), adjust(b)]
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("")
  );
}

// ============================================================================
// CSS variable mapping — re-skins shadcn semantic tokens for the preview.
// ============================================================================

/**
 * Map a portfolio theme onto the CSS custom properties the shared preview
 * components consume, so editing the panel re-skins the preview live. Also
 * exposes `--pf-*` variables for the preview's own headings/type scale.
 */
export function portfolioThemeToCssVars(
  theme: PortfolioTheme,
): React.CSSProperties {
  // Text colours as they will actually render (EPD-12471): body text sits on
  // the page background and on surface-filled cards and tabs, so each must
  // read on both; button text must read on the accent.
  const bodyBackgrounds = [theme.bg, theme.surface];
  const text = readableTextColor(theme.text, bodyBackgrounds);
  const heading = readableTextColor(theme.headingColor, bodyBackgrounds);
  const muted = readableTextColor(theme.mutedText, bodyBackgrounds);
  const accentText = readableTextColor(theme.accentText, [theme.accent]);
  return {
    // shadcn semantic tokens (consumed by reused public-profile components)
    ["--background" as string]: theme.bg,
    ["--foreground" as string]: text,
    ["--card" as string]: theme.bg,
    ["--card-foreground" as string]: text,
    ["--primary" as string]: theme.accent,
    ["--primary-foreground" as string]: accentText,
    ["--muted" as string]: theme.surface,
    ["--muted-foreground" as string]: muted,
    ["--accent" as string]: theme.surface,
    ["--accent-foreground" as string]: text,
    ["--border" as string]: theme.border,
    ["--ring" as string]: theme.accent,
    // Portfolio-specific tokens (consumed by the preview chrome)
    ["--pf-bg" as string]: theme.bg,
    ["--pf-surface" as string]: theme.surface,
    ["--pf-hero" as string]: theme.heroSolid,
    ["--pf-text" as string]: text,
    ["--pf-heading" as string]: heading,
    ["--pf-muted" as string]: muted,
    ["--pf-border" as string]: theme.border,
    ["--pf-accent" as string]: theme.accent,
    ["--pf-accent-text" as string]: accentText,
    ["--pf-font-heading" as string]: theme.fontHeading,
    ["--pf-font-body" as string]: theme.fontBody,
    ["--pf-h1" as string]: `${theme.h1Size}px`,
    ["--pf-h2" as string]: `${theme.h2Size}px`,
    ["--pf-h3" as string]: `${theme.h3Size}px`,
    ["--pf-body" as string]: `${theme.bodySize}px`,
    background: theme.bg,
    color: text,
    fontFamily: theme.fontBody,
  };
}

/**
 * Text tokens for the header band (EPD-12471), which sits on the theme's
 * header colour (`heroSolid`) rather than the page background: the same
 * colours as `portfolioThemeToCssVars`, made readable on the header colour.
 * Set on the band so its name, location, bio and icons pick them up.
 */
export function portfolioHeroCssVars(
  theme: PortfolioTheme,
): React.CSSProperties {
  const hero = [theme.heroSolid];
  const text = readableTextColor(theme.text, hero);
  const muted = readableTextColor(theme.mutedText, hero);
  return {
    ["--foreground" as string]: text,
    ["--muted-foreground" as string]: muted,
    ["--pf-text" as string]: text,
    ["--pf-heading" as string]: readableTextColor(theme.headingColor, hero),
    ["--pf-muted" as string]: muted,
    background: theme.heroSolid,
    color: text,
  };
}

// ============================================================================
// Persistence — profiles.metadata JSON
// ============================================================================

const THEME_KEY = "portfolioTheme";
const PUBLISHED_KEY = "portfolioPublished";

/**
 * Parse a theme JSON string (the inner value stored under the metadata's
 * `portfolioTheme` key), falling back to the default when absent or invalid.
 * Used on the public page, which receives only the theme value — not the whole
 * metadata blob — so unrelated metadata keys are never exposed.
 */
export function parsePortfolioThemeJson(
  raw: string | null | undefined,
): PortfolioTheme {
  if (typeof raw !== "string") return DEFAULT_PORTFOLIO_THEME;
  try {
    const candidate = portfolioThemeSchema.safeParse(JSON.parse(raw));
    return candidate.success ? candidate.data : DEFAULT_PORTFOLIO_THEME;
  } catch {
    return DEFAULT_PORTFOLIO_THEME;
  }
}

/** Read the stored theme from a profile's metadata, falling back to default. */
export function readPortfolioTheme(
  metadata: string | null | undefined,
): PortfolioTheme {
  const parsed = parseProfileMetadata(metadata);
  const raw = parsed[THEME_KEY];
  return parsePortfolioThemeJson(typeof raw === "string" ? raw : null);
}

/** Whether the profile has ever saved a portfolio theme (vs. defaulting). */
export function hasStoredPortfolioTheme(
  metadata: string | null | undefined,
): boolean {
  const parsed = parseProfileMetadata(metadata);
  return typeof parsed[THEME_KEY] === "string";
}

/**
 * The theme the editor initialises with (EPD-10123). For a brand-new portfolio
 * (no theme saved yet) the contact email is seeded from the creator's profile
 * email, so the Contact block is populated by default without exposing the
 * email through any public API — the creator can clear it, and once a theme is
 * saved its `contactEmail` is respected verbatim (an intentional clear sticks).
 */
export function initialPortfolioTheme(
  metadata: string | null | undefined,
  profileEmail: string | null | undefined,
): PortfolioTheme {
  const stored = readPortfolioTheme(metadata);
  if (
    !hasStoredPortfolioTheme(metadata) &&
    !stored.contactEmail &&
    profileEmail
  ) {
    return { ...stored, contactEmail: profileEmail };
  }
  return stored;
}

/** Read the published flag from a profile's metadata (defaults to false). */
export function readPortfolioPublished(
  metadata: string | null | undefined,
): boolean {
  const parsed = parseProfileMetadata(metadata);
  return parsed[PUBLISHED_KEY] === true;
}

/**
 * Serialize theme/published updates into a metadata string, merging with
 * whatever the profile already stores (so other metadata keys survive).
 *
 * Legacy `hiddenSections` (written by the old profile editor, still honoured by
 * the public-profile API) hides individual preference categories — all of
 * which render inside the portfolio's Highlights section. Section visibility is
 * now owned by the Sections panel, so saving a theme that shows Highlights
 * clears those legacy hides; otherwise the panel would show the section as on
 * while the API kept stripping its data.
 */
export function serializePortfolioMetadata(
  existing: string | null | undefined,
  updates: { theme?: PortfolioTheme; published?: boolean },
): string {
  const patch: Partial<ProfileMetadata> = {};
  if (updates.theme !== undefined) {
    patch[THEME_KEY] = JSON.stringify(updates.theme);
    if (
      updates.theme.show.highlights !== false &&
      getHiddenSections(existing).length > 0
    ) {
      patch.hiddenSections = [];
    }
  }
  if (updates.published !== undefined) {
    patch[PUBLISHED_KEY] = updates.published;
  }
  return serializeProfileMetadata(existing, patch);
}
